import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "map" ]

  connect() {
    // Esperar a que el DOM esté completamente listo
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeMap());
    } else {
      // Usar setTimeout para asegurar que el elemento esté renderizado
      setTimeout(() => this.initializeMap(), 200);
    }
  }

  disconnect() {
    // Limpiar el mapa cuando el controlador se desconecta
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  initializeMap() {
    // Esperar a que Mapbox GL JS esté disponible (ya está cargado en el layout)
    if (typeof window.mapboxgl === 'undefined') {
      setTimeout(() => {
        if (typeof window.mapboxgl !== 'undefined') {
          this.initializeMap();
        }
      }, 500);
      return;
    }

    try {
      // Verificar que el target del mapa existe
      if (!this.hasMapTarget) {
        return;
      }

      // Verificar que la API key está presente
      const apiKey = this.mapTarget.dataset.mapboxApiKey;
      if (!apiKey || apiKey.trim() === '') {
        return;
      }

      // Asegurar que el contenedor esté vacío (requisito de Mapbox)
      const mapContainer = this.mapTarget;
      if (mapContainer.innerHTML.trim() !== '') {
        mapContainer.innerHTML = '';
      }

      // Configurar el access token de Mapbox
      window.mapboxgl.accessToken = apiKey;

      // Verificar que el contenedor existe y es un elemento DOM válido
      if (!mapContainer || !(mapContainer instanceof HTMLElement)) {
        return;
      }

      // Parsear markers de forma segura
      let markers = [];
      try {
        const markersData = this.mapTarget.dataset.markers;
        if (markersData && markersData.trim() !== '' && markersData !== 'null' && markersData !== '[]') {
          markers = JSON.parse(markersData);
        }
      } catch (e) {
        markers = [];
      }

      // Verificar que markers es un array
      if (!Array.isArray(markers)) {
        markers = [];
      }

      // Filtrar markers válidos
      const validMarkers = markers.filter(marker =>
        marker &&
        typeof marker.lat === 'number' &&
        typeof marker.lng === 'number' &&
        !isNaN(marker.lat) &&
        !isNaN(marker.lng) &&
        marker.lat >= -90 && marker.lat <= 90 &&
        marker.lng >= -180 && marker.lng <= 180
      );

      // Determinar centro y zoom
      const defaultCenter = validMarkers.length > 0
        ? [validMarkers[0].lng, validMarkers[0].lat]
        : [-58.3816, -34.6037]; // Buenos Aires por defecto

      const defaultZoom = validMarkers.length > 0 ? 12 : 10;

      // Inicializar el mapa según la documentación oficial de Mapbox GL JS
      this.map = new window.mapboxgl.Map({
        container: mapContainer,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: defaultCenter,
        zoom: defaultZoom,
        attributionControl: true
      });

      // Esperar a que el mapa esté completamente cargado
      this.map.on('load', () => {
        this.addMarkers(validMarkers);
      });

      // Manejar el caso donde el mapa ya está cargado
      if (this.map.loaded()) {
        this.addMarkers(validMarkers);
      }
    } catch (e) {
      // Error silencioso
    }
  }

  addMarkers(markers) {
    if (!this.map || !Array.isArray(markers)) {
      return;
    }

    // Agregar markers al mapa
    markers.forEach((marker) => {
      try {
        const popup = new window.mapboxgl.Popup({ offset: 25 })
          .setHTML(marker.infoWindow || '');

        new window.mapboxgl.Marker({ color: '#3b82f6' })
          .setLngLat([marker.lng, marker.lat])
          .setPopup(popup)
          .addTo(this.map);
      } catch (e) {
        // Error silencioso
      }
    });

    // Ajustar el mapa a los markers si hay markers válidos
    if (markers.length >= 1) {
      const bounds = new window.mapboxgl.LngLatBounds();
      markers.forEach(marker => {
        bounds.extend([marker.lng, marker.lat]);
      });

      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds, {
          padding: { top: 70, bottom: 70, left: 70, right: 70 },
          maxZoom: 15,
          duration: 0
        });
      }
    }
  }

  loadMapboxScript() {
    return new Promise((resolve, reject) => {
      // Verificar si ya está cargado
      if (window.mapboxgl) {
        resolve();
        return;
      }

      // Cargar el script de Mapbox GL JS desde el CDN
      const script = document.createElement('script');
      script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js';
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Error al cargar Mapbox GL JS'));
      document.head.appendChild(script);
    });
  }
}
